// src/pages/ImportsPage.js
import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function ImportsPage() {
  const [importType, setImportType] = useState('members');
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [imported, setImported] = useState(null);

  // [marker: contact-list-import v1]
  const isExcel = importType.startsWith('qb-') || importType === 'contact-list';

  const handleValidate = async () => {
    if (!file) return toast.error(isExcel ? 'Please select an Excel (.xlsx) file.' : 'Please select a CSV file.');
    setValidating(true);
    setValidationResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/imports/${importType}/validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setValidationResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Validation failed.');
    } finally { setValidating(false); }
  };

  const handleConfirm = async () => {
    if (!validationResult?.readyToImport) return;
    if (!window.confirm(`Import ${validationResult.summary.validCount} records? This cannot be undone.`)) return;
    setImporting(true);
    try {
      const res = await api.post(`/imports/${importType}/confirm`);
      setImported(res.data);
      setValidationResult(null);
      setFile(null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed.');
    } finally { setImporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/imports/templates/${importType}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `njjba_${importType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download template.');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Import Data</h1>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        <div className="card-header"><span className="card-title">Data Import</span></div>
        <div className="card-body">
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 20 }}>
            {importType === 'contact-list'
              ? 'Upload the business-list spreadsheet (.xlsx) as-is — the sheet and columns are detected automatically. Contacts whose email already exists are skipped, and duplicate emails inside the file are only imported once. RSVP columns are ignored.'
              : isExcel
              ? 'Upload your unmodified QuickBooks Desktop export (.xlsx). The correct sheet is detected automatically — no template or clean-up needed.'
              : 'Import members or contacts from a CSV file. Download the template first, fill in your data, then upload here.'}
          </p>

          <div className="form-row" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Import Type</label>
              <select className="form-select" value={importType} onChange={e => { setImportType(e.target.value); setValidationResult(null); setFile(null); setImported(null); }}>
                <option value="members">Member Businesses (CSV)</option>
                <option value="contacts">Contacts (CSV)</option>
                <option value="contact-list">Contacts — Business List (.xlsx)</option>
                <option value="qb-members">Members — QuickBooks Export (.xlsx)</option>
                <option value="qb-member-details">Members — Update Contact Info / Salutations (.xlsx)</option>
                <option value="qb-invoices">Invoices — QuickBooks Export (.xlsx)</option>
                <option value="qb-payments">Payments — QuickBooks Export (.xlsx)</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              {isExcel ? (
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', paddingBottom: 8 }}>
                  {importType === 'contact-list'
                    ? 'No template needed — upload the spreadsheet as-is.'
                    : 'No template needed — upload the QuickBooks export as-is.'}
                </div>
              ) : (
                <button type="button" onClick={handleDownloadTemplate} className="btn btn-secondary">
                  📥 Download Template
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{isExcel ? 'Select Excel File (.xlsx)' : 'Select CSV File'}</label>
            <input type="file" accept={isExcel ? '.xlsx,.xls' : '.csv'} className="form-input" onChange={e => { setFile(e.target.files[0]); setValidationResult(null); }} />
          </div>

          <button className="btn btn-secondary" onClick={handleValidate} disabled={!file || validating}>
            {validating ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Validating...</> : '🔍 Validate File'}
          </button>

          {validationResult && (
            <div style={{ marginTop: 20 }}>
              <div style={{
                background: validationResult.readyToImport ? 'var(--success-bg)' : 'var(--warning-bg)',
                border: `1px solid ${validationResult.readyToImport ? 'var(--success)' : 'var(--warning)'}`,
                borderRadius: 'var(--radius)', padding: 16, marginBottom: 12,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: validationResult.readyToImport ? 'var(--success)' : 'var(--warning)' }}>
                  {validationResult.readyToImport ? '✅ Ready to import' : '⚠ Issues found — review before importing'}
                </div>
                <div style={{ fontSize: '0.875rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  <span>Total rows: <strong>{validationResult.summary.totalRows}</strong></span>
                  <span>Will import: <strong style={{ color: 'var(--success)' }}>{validationResult.summary.validCount}</strong></span>
                  <span>Duplicates: <strong style={{ color: 'var(--warning)' }}>{validationResult.summary.duplicateCount}</strong></span>
                  <span>Skipped (no name): <strong style={{ color: validationResult.summary.errorCount > 0 ? 'var(--danger)' : undefined }}>{validationResult.summary.errorCount}</strong></span>
                </div>
                {validationResult.details && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginTop: 8, borderTop: '1px solid var(--gray-200)', paddingTop: 8 }}>
                    Already in Contacts (skipped): <strong>{validationResult.details.dupInDb}</strong>{' · '}
                    Duplicate inside file (skipped): <strong>{validationResult.details.dupInSheet}</strong>{' · '}
                    Imported without an email: <strong>{validationResult.details.noEmailCount}</strong>
                  </div>
                )}
              </div>

              {validationResult.errors?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--danger)', marginBottom: 6 }}>Errors (must fix):</div>
                  {validationResult.errors.slice(0, 5).map((e, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', color: 'var(--danger)', padding: '3px 0' }}>Row {e.row}: {e.errors?.join(', ')}</div>
                  ))}
                </div>
              )}

              {validationResult.warnings?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--warning)', marginBottom: 6 }}>Warnings:</div>
                  {validationResult.warnings.slice(0, 5).map((w, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', color: 'var(--warning)', padding: '3px 0' }}>Row {w.row}: {w.warnings?.join(', ')}</div>
                  ))}
                </div>
              )}

              {validationResult.readyToImport && (
                <button className="btn btn-primary" onClick={handleConfirm} disabled={importing}>
                  {importing ? 'Importing...' : `✅ Confirm Import (${validationResult.summary.validCount} records)`}
                </button>
              )}
            </div>
          )}

          {imported && (
            <div style={{ marginTop: 20, background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: 'var(--radius)', padding: 16 }}>
              <div style={{ color: 'var(--success)', fontWeight: 700 }}>✅ Import Complete</div>
              <div style={{ fontSize: '0.875rem', marginTop: 4 }}>
                {imported.inserted} records imported · {imported.skipped || 0} skipped (duplicates)
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
